import { useQuery } from '@apollo/client';
import { createFileRoute, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { endpointPortfolioConstituentBreakdownGET } from '~/api/def/endpoints/portfolioAnalysisEndpoint';
import { AssetBreakdownWidget } from '~/components/ui/widgets/AssetBreakdownWidget/AssetBreakdownWidget';
import { GET_LATEST_SNAPSHOT_ID } from '~/graphql/queries/portfolio';

type SearchParams = {
  portfolioId?: string;
  snapshotId?: string;
  scenario?: 'PROPOSED' | 'CURRENT_HOLDINGS' | 'BENCHMARK';
};

export const Route = createFileRoute('/_authLayout/debug/asset-breakdown')({
  component: DebugAssetBreakdown,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    return {
      portfolioId: search.portfolioId as string | undefined,
      snapshotId: search.snapshotId as string | undefined,
      scenario:
        (search.scenario as 'PROPOSED' | 'CURRENT_HOLDINGS' | 'BENCHMARK') ||
        'PROPOSED',
    };
  },
});

function DebugAssetBreakdown() {
  const {
    portfolioId,
    snapshotId,
    scenario = 'PROPOSED',
  } = useSearch({
    from: '/_authLayout/debug/asset-breakdown',
  });
  const [data, setData] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [actualSnapshotId, setActualSnapshotId] = useState<string | null>(null);

  // Get latest snapshot ID
  const { data: snapshotData } = useQuery(GET_LATEST_SNAPSHOT_ID, {
    variables: { portfolioId },
    skip: !portfolioId,
  });

  // Fetch breakdown data
  const fetchData = useCallback(async () => {
    if (!snapshotId && !portfolioId) return;

    try {
      setIsLoading(true);
      setError(null);

      // Get latest snapshot if not provided
      let targetSnapshotId = snapshotId;
      if (
        !targetSnapshotId &&
        snapshotData?.EQPortfolio?.[0]?.EQPortfolioSnapshots?.[0]?.id
      ) {
        targetSnapshotId =
          snapshotData.EQPortfolio[0].EQPortfolioSnapshots[0].id;
      }

      if (!targetSnapshotId) {
        throw new Error('No snapshot ID available');
      }

      setActualSnapshotId(targetSnapshotId);

      const response = await endpointPortfolioConstituentBreakdownGET({
        snapshot_id: targetSnapshotId,
        scenario: scenario,
        horizon_days: 10,
      });

      setData(response);
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [snapshotId, portfolioId, snapshotData, scenario]);

  useEffect(() => {
    if (portfolioId) {
      fetchData();
    }
  }, [portfolioId, fetchData]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div
      style={{
        padding: '20px',
        backgroundColor: '#1a1a1a',
        minHeight: '100vh',
      }}
    >
      <h1 style={{ color: 'white', marginBottom: '20px' }}>
        Asset Breakdown Widget - Debug Page
      </h1>

      {!portfolioId ? (
        <div style={{ color: '#888' }}>
          <p>Add query parameters to load real data:</p>
          <p>?portfolioId=YOUR_PORTFOLIO_ID&scenario=PROPOSED</p>
          <p>
            Example:
            ?portfolioId=0199e3bb-61d5-7825-96b3-6d2cc0d96a9c&scenario=PROPOSED
          </p>
          <p>Scenarios: PROPOSED | CURRENT_HOLDINGS | BENCHMARK</p>
        </div>
      ) : (
        <>
          <p style={{ color: '#888', marginBottom: '20px' }}>
            Portfolio ID: {portfolioId}
            <br />
            Snapshot ID: {snapshotId || 'latest'}
            <br />
            Scenario: {scenario}
          </p>

          <div
            style={{
              height: '700px',
              backgroundColor: '#2a2a2a',
              padding: '20px',
            }}
          >
            <AssetBreakdownWidget
              data={data || null}
              isLoading={isLoading}
              error={error}
              refetch={refetch}
              widgetId="debug-asset-breakdown"
              title="Forward Asset Breakdown"
              snapshotId={actualSnapshotId}
              scenario={scenario}
            />
          </div>

          {error && (
            <div
              style={{
                marginTop: '20px',
                color: 'red',
                padding: '20px',
                backgroundColor: '#3a2a2a',
              }}
            >
              <h3>Error:</h3>
              <pre>{error.message}</pre>
              <pre>{error.stack}</pre>
            </div>
          )}

          {data && (
            <div style={{ marginTop: '20px', color: 'white' }}>
              <h3>Breakdown Info:</h3>
              <ul>
                <li>Scenario: {data.scenario}</li>
                <li>
                  Number of constituents: {data.constituents?.length || 0}
                </li>
                <li>
                  Expected Annual Return:{' '}
                  {data.portfolio_expected_annual_return?.toFixed(2)}%
                </li>
                <li>
                  Projected Volatility:{' '}
                  {data.portfolio_projected_volatility?.toFixed(2)}%
                </li>
              </ul>
              <h3>Sample Constituents (first 3):</h3>
              {data.constituents?.slice(0, 3).map((c: any, i: number) => (
                <div key={i} style={{ marginBottom: '10px' }}>
                  <p>
                    <strong>{c.name || c.ticker || c.isin}</strong>
                  </p>
                  <p>Allocation: {c.allocation_percent.toFixed(2)}%</p>
                  <p>Expected Return: {c.expected_annual_return.toFixed(2)}%</p>
                  <p>Data points: {c.dates?.length || 0}</p>
                  <p>
                    Final cumulative return:{' '}
                    {c.cumulative_returns[
                      c.cumulative_returns.length - 1
                    ]?.toFixed(2)}
                    %
                  </p>
                </div>
              ))}
            </div>
          )}

          {isLoading && (
            <div style={{ marginTop: '20px', color: 'yellow' }}>
              <p>Loading...</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
